/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { userSessionIssuersCimdClientsCreate } from "../funcs/userSessionIssuersCimdClientsCreate.js";
import { userSessionIssuersCimdClientsDelete } from "../funcs/userSessionIssuersCimdClientsDelete.js";
import { userSessionIssuersCimdClientsGet } from "../funcs/userSessionIssuersCimdClientsGet.js";
import { userSessionIssuersCimdClientsList } from "../funcs/userSessionIssuersCimdClientsList.js";
import { userSessionIssuersCimdClientsListPresets } from "../funcs/userSessionIssuersCimdClientsListPresets.js";
import { userSessionIssuersCimdClientsVerifyURL } from "../funcs/userSessionIssuersCimdClientsVerifyURL.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { CreateUserSessionIssuerCimdClientResult } from "../models/components/createusersessionissuercimdclientresult.js";
import { ListCimdClientPresetsResult } from "../models/components/listcimdclientpresetsresult.js";
import { UserSessionIssuerCimdClient } from "../models/components/usersessionissuercimdclient.js";
import { VerifyCimdURLResult } from "../models/components/verifycimdurlresult.js";
import {
  CreateUserSessionIssuerCimdClientRequest,
  CreateUserSessionIssuerCimdClientSecurity,
} from "../models/operations/createusersessionissuercimdclient.js";
import {
  DeleteUserSessionIssuerCimdClientRequest,
  DeleteUserSessionIssuerCimdClientSecurity,
} from "../models/operations/deleteusersessionissuercimdclient.js";
import {
  GetUserSessionIssuerCimdClientRequest,
  GetUserSessionIssuerCimdClientSecurity,
} from "../models/operations/getusersessionissuercimdclient.js";
import {
  ListCimdClientPresetsRequest,
  ListCimdClientPresetsSecurity,
} from "../models/operations/listcimdclientpresets.js";
import {
  ListUserSessionIssuerCimdClientsRequest,
  ListUserSessionIssuerCimdClientsResponse,
  ListUserSessionIssuerCimdClientsSecurity,
} from "../models/operations/listusersessionissuercimdclients.js";
import {
  VerifyUserSessionIssuerCimdClientURLRequest,
  VerifyUserSessionIssuerCimdClientURLSecurity,
} from "../models/operations/verifyusersessionissuercimdclienturl.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class UserSessionIssuersCimdClients extends ClientSDK {
  /**
   * createUserSessionIssuerCimdClient userSessionIssuersCimdClients
   *
   * @remarks
   * Allow an additional CIMD document URL on a user_session_issuer, beyond the preset catalog. The URL is validated for draft-ietf-oauth-client-id-metadata-document-02 §3 syntax and rejected outright when malformed. The document itself is deliberately NOT fetched here: a vendor's host being briefly unreachable must not block configuration, and an advisory warning nobody can act on is not worth an outbound request on every write. Call verifyURL first to check that the document is reachable and valid.
   */
  async create(
    request: CreateUserSessionIssuerCimdClientRequest,
    security?: CreateUserSessionIssuerCimdClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CreateUserSessionIssuerCimdClientResult> {
    return unwrapAsync(userSessionIssuersCimdClientsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteUserSessionIssuerCimdClient userSessionIssuersCimdClients
   *
   * @remarks
   * Remove a custom CIMD document URL from a user_session_issuer. New authorization requests from that client are denied immediately; sessions already issued to it are unaffected and continue until they expire.
   */
  async delete(
    request: DeleteUserSessionIssuerCimdClientRequest,
    security?: DeleteUserSessionIssuerCimdClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(userSessionIssuersCimdClientsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getUserSessionIssuerCimdClient userSessionIssuersCimdClients
   *
   * @remarks
   * Get a single custom CIMD document URL entry by id.
   */
  async get(
    request: GetUserSessionIssuerCimdClientRequest,
    security?: GetUserSessionIssuerCimdClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<UserSessionIssuerCimdClient> {
    return unwrapAsync(userSessionIssuersCimdClientsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listUserSessionIssuerCimdClients userSessionIssuersCimdClients
   *
   * @remarks
   * List the custom CIMD document URLs configured on a user_session_issuer. Does not include the preset catalog — call listPresets for that.
   */
  async list(
    request: ListUserSessionIssuerCimdClientsRequest,
    security?: ListUserSessionIssuerCimdClientsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<
    PageIterator<ListUserSessionIssuerCimdClientsResponse, { cursor: string }>
  > {
    return unwrapResultIterator(userSessionIssuersCimdClientsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listPresets userSessionIssuersCimdClients
   *
   * @remarks
   * List Speakeasy's curated CIMD preset catalog. Issuers whose admission mode is 'presets' admit every enabled entry here automatically, with no per-issuer configuration. Presets mode is opt-in; an issuer without an explicit mode uses 'open' and evaluates this catalog only for its shadow measurement. The catalog is global and contains no tenant data.
   */
  async listPresets(
    request?: ListCimdClientPresetsRequest | undefined,
    security?: ListCimdClientPresetsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListCimdClientPresetsResult> {
    return unwrapAsync(userSessionIssuersCimdClientsListPresets(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * verifyURL userSessionIssuersCimdClients
   *
   * @remarks
   * Check that a CIMD document URL is reachable and spec-compliant, without saving anything. A pre-flight for create: the same fetch and validation the authorization server performs, reported in full so an operator can fix the URL before adding it. Every probe outcome is a 200 with verified true or false — errors are reserved for a malformed request, missing authorization, or an exceeded rate limit. Rate limited per project, since this is the one endpoint that makes Speakeasy fetch a caller-chosen URL.
   */
  async verifyURL(
    request: VerifyUserSessionIssuerCimdClientURLRequest,
    security?: VerifyUserSessionIssuerCimdClientURLSecurity | undefined,
    options?: RequestOptions,
  ): Promise<VerifyCimdURLResult> {
    return unwrapAsync(userSessionIssuersCimdClientsVerifyURL(
      this,
      request,
      security,
      options,
    ));
  }
}
